import { useMemo, useState } from 'preact/hooks'
import { DEFAULT_EMOJI_SIZE } from '../constants'
import type { Content, EmojiContent } from '../types'
import { replaceContents } from '../utils/contents'
import { searchEmoji } from '../utils/emojiData'
import { inputChecked, inputValue } from '../utils/input'
import { isAscii } from '../utils/string'
import { Button, ButtonRow } from './Button'
import { Field } from './Field'
import { Panel } from './Panel'
import { SliderRow } from './SliderRow'
import { TwemojiImg } from './TwemojiImg'

interface Props {
  value: Content[]
  onChange: (contents: Content[]) => void
  useTwemoji: boolean
  onTwemojiChange: (useTwemoji: boolean) => void
}

const MAX_RESULTS = 64

export function EmojiPicker({ value, onChange, useTwemoji, onTwemojiChange }: Props) {
  const [search, setSearch] = useState('')
  const [activeId, setActiveId] = useState<string | null>(null)

  const emojis = value.filter((item): item is EmojiContent => item.type === 'emoji')
  const active = emojis.find((item) => item.id === activeId) ?? emojis[emojis.length - 1] ?? null
  const filtered = useMemo(() => searchEmoji(search, MAX_RESULTS), [search])

  const setEmojis = (next: EmojiContent[]) => {
    onChange(replaceContents(value, 'emoji', next))
  }

  const addEmoji = (emoji: string) => {
    const id = crypto.randomUUID()

    setSearch('')
    setEmojis([...emojis, { type: 'emoji', id, emoji, x: 0, y: 0, size: DEFAULT_EMOJI_SIZE }])
    setActiveId(id)
  }

  const removeById = (id: string) => {
    setEmojis(emojis.filter((item) => item.id !== id))

    if (activeId === id) {
      setActiveId(null)
    }
  }

  const updateById = (id: string, patch: Partial<EmojiContent>) => {
    setEmojis(emojis.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }

  const onInput = (event: Event) => {
    const next = inputValue(event)

    if (next.length > 0 && !isAscii(next)) {
      addEmoji(next)
    } else {
      setSearch(next)
    }
  }

  const onToggle = (event: Event) => {
    const enabled = inputChecked(event)

    onTwemojiChange(enabled)

    if (!enabled) {
      setSearch('')
      setEmojis([])
    }
  }

  return (
    <Panel title="Emoji" description="Render emojis as bundled Twemoji SVGs instead of system fonts.">
      <Field label="Use Twemoji">
        <input type="checkbox" checked={useTwemoji} onChange={onToggle} />
      </Field>
      {useTwemoji && (
        <>
          {active && (
            <div class="emoji-active-row">
              <div class="emoji-stack">
                {emojis.map((item) => (
                  <div key={item.id} class={`emoji-stack-item${item.id === active.id ? ' active' : ''}`}>
                    <button type="button" class="emoji-stack-select" title="Edit" onClick={() => setActiveId(item.id)}>
                      <TwemojiImg emoji={item.emoji} className="emoji-twemoji" />
                    </button>
                    <button type="button" class="emoji-stack-remove" title="Remove" onClick={() => removeById(item.id)}>
                      −
                    </button>
                  </div>
                ))}
              </div>
              <SliderRow
                label="Size"
                min={12}
                max={64}
                value={active.size}
                onChange={(size) => updateById(active.id, { size })}
              />
              <ButtonRow>
                <Button variant="secondary" onClick={() => updateById(active.id, { x: 0, y: 0 })}>
                  Center
                </Button>
              </ButtonRow>
            </div>
          )}
          <Field>
            <input type="text" placeholder="Search keywords or paste an emoji…" value={search} onInput={onInput} />
          </Field>
          {filtered.length === 0 ? (
            <p class="emoji-empty">No matches.</p>
          ) : (
            <div class="emoji-grid">
              {filtered.map((entry) => (
                <button
                  key={entry.char}
                  type="button"
                  title={entry.name}
                  class="emoji-btn"
                  onClick={() => addEmoji(entry.char)}
                >
                  <TwemojiImg emoji={entry.char} className="emoji-twemoji" />
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  )
}
